import {
  MembershipVisibilitySchema,
  SpaceRoleSchema,
  type Invite,
  type MembershipVisibility,
  type Project,
  type SpaceRole,
} from "@scenix/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApiError, api, type Member } from "../lib/api";
import { Crumbs } from "../components/Crumbs";
import { useSession } from "../lib/session";

const ROLE_LABELS: Record<SpaceRole, string> = {
  viewer: "Viewer",
  editor: "Editor",
  admin: "Admin",
  owner: "Owner",
};

/** What each visibility offers a person, rather than the word the wire uses. */
const VISIBILITY_LABELS: Record<MembershipVisibility, string> = {
  all: "Every project in this space",
  listed: "Only the projects ticked below",
};

/**
 * Who is in a space, at what role, and who has been invited but has not
 * signed in yet.
 *
 * Membership is by invitation to an email address, never by directory search
 * (ADR 000008): there is no way to look somebody up, only to address an
 * invite to an address already known. Redemption happens on that person's own
 * first Google sign-in, not here.
 */
export function MembersRoute() {
  const { spaceId = "" } = useParams();
  const { session } = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<SpaceRole>("editor");
  // What the membership this invite redeems into will see (ADR 000096). An
  // owner always sees the whole space, so choosing that role takes the choice
  // away rather than letting an admin build a request the API refuses.
  const [wantedVisibility, setWantedVisibility] = useState<MembershipVisibility>("all");
  const [inviteProjects, setInviteProjects] = useState<string[]>([]);
  const inviteVisibility: MembershipVisibility = role === "owner" ? "all" : wantedVisibility;
  const [failure, setFailure] = useState<string | null>(null);
  // A membership is kept for the life of the space (ADR 000059), so the list
  // holds everybody who ever arrived. Who is here now is the everyday question,
  // so that is the default and the rest is one click away.
  const [showPast, setShowPast] = useState(false);
  // The same argument for invitations that ran out: who could still take one up
  // is the everyday question, and the dead ones are kept because they say who
  // was invited and when it lapsed (ADR 000100).
  const [showRanOut, setShowRanOut] = useState(false);

  const space = useQuery({
    queryKey: ["space", spaceId],
    queryFn: () => api.getSpace(spaceId as never),
  });

  const members = useQuery({
    queryKey: ["members", spaceId],
    queryFn: () => api.listMembers(spaceId as never),
  });

  // Admin governs who is in a space and at what role (ADR 000036); everybody
  // else still sees who they are working with, just without the controls.
  const isAdmin = space.data?.role === "admin" || space.data?.role === "owner";

  const invites = useQuery({
    queryKey: ["invites", spaceId],
    queryFn: () => api.listInvites(spaceId as never),
    enabled: isAdmin,
  });

  // Only an admin may change visibility, and only an admin sees the whole
  // space: this listing is narrowed by the caller's own visibility, so anybody
  // else would be shown a picker missing the projects they cannot see
  // (ADR 000046).
  const projects = useQuery({
    queryKey: ["projects", spaceId],
    queryFn: () => api.listProjects(spaceId as never),
    enabled: isAdmin,
  });

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["members", spaceId] }),
      queryClient.invalidateQueries({ queryKey: ["invites", spaceId] }),
    ]);

  const invite = useMutation({
    mutationFn: () =>
      api.createInvite(spaceId as never, {
        email: email.trim(),
        role,
        visibility: inviteVisibility,
        // Only sent when it means something, the same as the member row's
        // save: an allow-list alongside "all" would read as though it were
        // being kept.
        ...(inviteVisibility === "listed" ? { projectIds: inviteProjects } : {}),
      }),
    onSuccess: async () => {
      setEmail("");
      setWantedVisibility("all");
      setInviteProjects([]);
      setFailure(null);
      await invalidate();
    },
    onError: (error) => setFailure(describe(error)),
  });

  const revokeInvite = useMutation({
    mutationFn: (inviteId: Invite["id"]) => api.revokeInvite(spaceId as never, inviteId),
    onSuccess: () => invalidate(),
    onError: (error) => setFailure(describe(error)),
  });

  const changeRole = useMutation({
    mutationFn: (vars: { userId: Member["user"]["id"]; role: SpaceRole }) =>
      api.updateMemberRole(spaceId as never, vars.userId, { role: vars.role }),
    onSuccess: () => invalidate(),
    onError: (error) => setFailure(describe(error)),
  });

  const remove = useMutation({
    mutationFn: (userId: Member["user"]["id"]) => api.removeMember(spaceId as never, userId),
    onSuccess: async (_data, userId) => {
      // Leaving yourself takes the space off the list you can still reach,
      // not just this now-inaccessible member row.
      if (session.state === "signed-in" && userId === session.user.id) {
        await queryClient.invalidateQueries({ queryKey: ["spaces"] });
        navigate("/spaces");
        return;
      }
      await invalidate();
    },
    onError: (error) => setFailure(describe(error)),
  });

  const reactivate = useMutation({
    mutationFn: (userId: Member["user"]["id"]) => api.reactivateMember(spaceId as never, userId),
    onSuccess: () => invalidate(),
    onError: (error) => setFailure(describe(error)),
  });

  const everybody = members.data ?? [];
  const past = everybody.filter((m) => m.status === "inactive");
  const shownMembers = showPast ? everybody : everybody.filter((m) => m.status !== "inactive");

  if (space.isPending) return <p role="status">Loading…</p>;
  if (space.error) {
    return (
      <section className="empty">
        <h1>Could not load this space</h1>
        <p>
          <Link to={`/spaces/${spaceId}`}>Back</Link>
        </p>
      </section>
    );
  }

  const pending = invites.data?.filter((i) => i.status === "pending" && !ranOut(i)) ?? [];
  const ranOutInvites = invites.data?.filter(ranOut) ?? [];
  // Accepted still grants sign-in on its own (ADR 000040): removing the
  // membership does not touch it, so revoking it is the other half of fully
  // removing somebody. Redundant grants exactly the same and is revoked the
  // same way, so it belongs in the same list rather than nowhere (ADR 000098).
  const standing =
    invites.data?.filter((i) => i.status === "accepted" || i.status === "redundant") ?? [];

  // Narrowing to nothing is a coherent thing to ask the API for and is almost
  // never what somebody who has not ticked anything meant, so the button waits
  // for the ticks rather than sending an empty allow-list (ADR 000096).
  const canInvite =
    email.trim() !== "" &&
    !invite.isPending &&
    !(inviteVisibility === "listed" && inviteProjects.length === 0);

  return (
    <section>
      <header className="page-head">
        <Crumbs spaceId={spaceId} />
        <h1>Members</h1>
      </header>

      {failure && (
        <p role="alert" className="error">
          {failure}
        </p>
      )}

      {isAdmin && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (canInvite) invite.mutate();
          }}
        >
          <div className="row">
            <input
              type="email"
              aria-label="Email to invite"
              placeholder="name@example.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <select
              aria-label="Role"
              value={role}
              onChange={(event) => setRole(event.target.value as SpaceRole)}
            >
              {SpaceRoleSchema.options.map((value) => (
                <option key={value} value={value}>
                  {ROLE_LABELS[value]}
                </option>
              ))}
            </select>
            <select
              aria-label="What they can see"
              value={inviteVisibility}
              disabled={role === "owner"}
              onChange={(event) => setWantedVisibility(event.target.value as MembershipVisibility)}
            >
              {MembershipVisibilitySchema.options.map((value) => (
                <option key={value} value={value}>
                  {VISIBILITY_LABELS[value]}
                </option>
              ))}
            </select>
            <button type="submit" disabled={!canInvite}>
              {invite.isPending ? "Inviting…" : "Invite"}
            </button>
          </div>

          {inviteVisibility === "listed" &&
            (projects.isError ? (
              <p className="error">Could not load this space's projects.</p>
            ) : projects.data === undefined ? (
              <p className="muted">Loading projects…</p>
            ) : projects.data.length === 0 ? (
              <p className="muted">This space has no projects yet.</p>
            ) : (
              <ul>
                {projects.data.map((project) => (
                  <li key={project.id}>
                    <label>
                      <input
                        type="checkbox"
                        checked={inviteProjects.includes(project.id)}
                        onChange={(event) =>
                          setInviteProjects((current) =>
                            event.target.checked
                              ? [...current, project.id]
                              : current.filter((id) => id !== project.id),
                          )
                        }
                      />
                      {project.name}
                    </label>
                  </li>
                ))}
              </ul>
            ))}
        </form>
      )}

      {isAdmin && pending.length > 0 && (
        <>
          <h2 className="section-head">Pending</h2>
          <ul className="keys">
            {pending.map((i) => (
              <li key={i.id}>
                <span className="key-body">
                  <strong>{i.email}</strong>
                  <span className="key-meta">
                    invited as {ROLE_LABELS[i.role]} · expires {shortDate(i.expiresAt)}
                  </span>
                  {/* What it will redeem into, while that is still a
                      prediction. Once accepted the membership is the answer
                      and this would only be what somebody once intended
                      (ADR 000096). */}
                  {i.visibility === "listed" && (
                    <span className="key-meta">
                      {describeVisibility(i.visibility, i.visibleProjectIds, projects.data)}
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  className="ghost danger"
                  disabled={revokeInvite.isPending}
                  onClick={() => revokeInvite.mutate(i.id)}
                >
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Kept rather than hidden away: an invitation that lapsed is the thing
          an admin has to see to work out why somebody is being refused, and
          the repair is the form above with the same address (ADR 000100). */}
      {isAdmin && ranOutInvites.length > 0 && (
        <>
          <p className="muted">
            <button type="button" className="ghost" onClick={() => setShowRanOut((was) => !was)}>
              {showRanOut
                ? "Hide the invitations that ran out"
                : `Show ${ranOutInvites.length} ${
                    ranOutInvites.length === 1 ? "invitation" : "invitations"
                  } that ran out`}
            </button>
          </p>
          {showRanOut && (
            <ul className="keys">
              {ranOutInvites.map((i) => (
                <li key={i.id}>
                  <span className="key-body">
                    <strong>{i.email}</strong>
                    <span className="key-meta">
                      invited as {ROLE_LABELS[i.role]} · ran out {shortDate(i.expiresAt)} · invite
                      them again to let them in
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {isAdmin && standing.length > 0 && (
        <>
          <h2 className="section-head">Standing access</h2>
          <ul className="keys">
            {standing.map((i) => (
              <li key={i.id}>
                <span className="key-body">
                  <strong>{i.email}</strong>
                  {/* A redundant invite says so where somebody would otherwise
                      read it as the invitation having done what it looked like
                      it did (ADR 000098). */}
                  <span className="key-meta">
                    {i.status === "redundant"
                      ? `invited as ${ROLE_LABELS[i.role]} · they were already in this space, so this changed nothing`
                      : `invited as ${ROLE_LABELS[i.role]} · accepted`}
                  </span>
                </span>
                <button
                  type="button"
                  className="ghost danger"
                  disabled={revokeInvite.isPending}
                  onClick={() => revokeInvite.mutate(i.id)}
                >
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      <h2 className="section-head">In this space</h2>

      {past.length > 0 && (
        <p className="muted">
          <button type="button" className="ghost" onClick={() => setShowPast((was) => !was)}>
            {showPast
              ? "Hide people who have left"
              : `Show ${past.length} who ${past.length === 1 ? "has" : "have"} left`}
          </button>
        </p>
      )}

      {members.isPending ? (
        <p role="status">Loading…</p>
      ) : members.error ? (
        <p className="error">{describe(members.error)}</p>
      ) : (
        <ul className="keys">
          {shownMembers.map((member) => {
            const isSelf = session.state === "signed-in" && session.user.id === member.user.id;
            const gone = member.status === "inactive";
            return (
              <li key={member.user.id} className={gone ? "is-inactive" : ""}>
                <span className="key-body">
                  <strong>{member.user.displayName}</strong>
                  <span className="key-meta">
                    {member.user.email}
                    {isSelf && " · you"}
                    {gone &&
                      ` · left${
                        member.deactivatedAt
                          ? ` ${new Date(member.deactivatedAt).toLocaleDateString()}`
                          : ""
                      }`}
                  </span>
                  {/* Somebody who left sees nothing at all, so what they would
                      see is not a fact about the space until they are back. */}
                  {!gone && (
                    <MemberVisibility
                      // Remount on what the server last said, so a change made
                      // elsewhere replaces a stale draft rather than hiding
                      // behind it.
                      key={`${member.visibility}:${member.visibleProjectIds.join(",")}`}
                      spaceId={spaceId}
                      member={member}
                      projects={projects.data}
                      projectsFailed={projects.isError}
                      // An owner administers the whole space and the API
                      // refuses to narrow one (ADR 000046), so the control is
                      // not offered rather than offered and refused.
                      canEdit={isAdmin && member.role !== "owner"}
                      onFailure={setFailure}
                    />
                  )}
                </span>
                {gone ? (
                  /* Somebody who left keeps their record, so the only thing to
                     offer is giving it back (ADR 000059). Their role is shown
                     because it is what they would come back as. */
                  <span className="row">
                    <span className="key-meta">{ROLE_LABELS[member.role]}</span>
                    {isAdmin && (
                      <button
                        type="button"
                        className="ghost"
                        disabled={reactivate.isPending}
                        onClick={() => reactivate.mutate(member.user.id)}
                      >
                        Give access back
                      </button>
                    )}
                  </span>
                ) : isAdmin ? (
                  <span className="row">
                    <select
                      aria-label={`Role for ${member.user.displayName}`}
                      value={member.role}
                      disabled={changeRole.isPending}
                      onChange={(event) =>
                        changeRole.mutate({
                          userId: member.user.id,
                          role: event.target.value as SpaceRole,
                        })
                      }
                    >
                      {SpaceRoleSchema.options.map((value) => (
                        <option key={value} value={value}>
                          {ROLE_LABELS[value]}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="ghost danger"
                      disabled={remove.isPending}
                      onClick={() => {
                        if (window.confirm(`Remove ${member.user.displayName} from this space?`)) {
                          remove.mutate(member.user.id);
                        }
                      }}
                    >
                      Remove
                    </button>
                  </span>
                ) : isSelf ? (
                  <span className="row">
                    <span className="key-meta">{ROLE_LABELS[member.role]}</span>
                    <button
                      type="button"
                      className="ghost danger"
                      disabled={remove.isPending}
                      onClick={() => {
                        if (window.confirm("Leave this space?")) {
                          remove.mutate(member.user.id);
                        }
                      }}
                    >
                      Leave
                    </button>
                  </span>
                ) : (
                  <span className="key-meta">{ROLE_LABELS[member.role]}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/**
 * What one member may see, and for an admin the control that changes it
 * (ADR 000046).
 *
 * A component of its own because the draft belongs to one member: a half-made
 * choice about the outside director has nothing to do with the row above it,
 * and hoisting it would leave the route holding a map keyed by user id.
 *
 * An unnarrowed member says nothing to a reader who cannot change it. The
 * everyday state is "sees everything", and printing that on every row would
 * bury the one row where it is not true.
 */
function MemberVisibility({
  spaceId,
  member,
  projects,
  projectsFailed,
  canEdit,
  onFailure,
}: {
  spaceId: string;
  member: Member;
  projects: Project[] | undefined;
  projectsFailed: boolean;
  canEdit: boolean;
  onFailure: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const [visibility, setVisibility] = useState<MembershipVisibility>(member.visibility);
  const [chosen, setChosen] = useState<Member["visibleProjectIds"]>(member.visibleProjectIds);

  const save = useMutation({
    mutationFn: () =>
      api.updateMemberVisibility(spaceId as never, member.user.id, {
        visibility,
        // Only sent when it means something: widening back to "all" clears the
        // stored list, so sending one alongside would read as though it were
        // being kept.
        ...(visibility === "listed" ? { projectIds: chosen } : {}),
      }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["members", spaceId] }),
        // An admin may narrow themselves, and every project listing is
        // filtered by the caller's own visibility (ADR 000046): leaving this
        // alone would show them projects they just stopped being able to open.
        queryClient.invalidateQueries({ queryKey: ["projects", spaceId] }),
      ]),
    onError: (error) => onFailure(describe(error)),
  });

  const summary = describeVisibility(member.visibility, member.visibleProjectIds, projects);

  if (!canEdit) {
    return member.visibility === "listed" ? <span className="key-meta">{summary}</span> : null;
  }

  const unchanged =
    visibility === member.visibility &&
    (visibility === "all" || sameIds(chosen, member.visibleProjectIds));

  // Narrowing to a list nobody has seen yet is how somebody ends up narrowed to
  // nothing by accident: the ticks are the whole instruction, and until the
  // projects arrive there are none to tick.
  const listUnknown = visibility === "listed" && projects === undefined;

  return (
    <details className="member-visibility">
      <summary>{summary}</summary>

      <fieldset>
        <legend className="key-meta">What {member.user.displayName} can see</legend>
        {MembershipVisibilitySchema.options.map((value) => (
          <label key={value}>
            <input
              type="radio"
              name={`visibility-${member.user.id}`}
              value={value}
              checked={visibility === value}
              onChange={() => setVisibility(value)}
            />
            {VISIBILITY_LABELS[value]}
          </label>
        ))}
      </fieldset>

      {visibility === "listed" &&
        (projectsFailed ? (
          <p className="error">Could not load this space's projects.</p>
        ) : projects === undefined ? (
          <p className="muted">Loading projects…</p>
        ) : projects.length === 0 ? (
          <p className="muted">This space has no projects yet.</p>
        ) : (
          <ul>
            {projects.map((project) => (
              <li key={project.id}>
                <label>
                  <input
                    type="checkbox"
                    checked={chosen.includes(project.id)}
                    onChange={(event) =>
                      setChosen((current) =>
                        event.target.checked
                          ? [...current, project.id]
                          : current.filter((id) => id !== project.id),
                      )
                    }
                  />
                  {project.name}
                </label>
              </li>
            ))}
          </ul>
        ))}

      <button
        type="button"
        disabled={unchanged || listUnknown || save.isPending}
        onClick={() => save.mutate()}
      >
        {save.isPending ? "Saving…" : "Save what they can see"}
      </button>
    </details>
  );
}

/**
 * One line saying what a membership can see, named rather than counted
 * wherever the names are to hand. Shared with a pending invite, which says the
 * same thing about the membership it has yet to create (ADR 000096).
 *
 * A project the listing does not carry is reported as a number rather than
 * dropped: an archived project still narrows somebody, and silently naming
 * fewer projects than the membership holds would understate their access.
 */
function describeVisibility(
  visibility: MembershipVisibility,
  ids: readonly string[],
  projects: Project[] | undefined,
): string {
  if (visibility === "all") return "Sees every project";

  if (ids.length === 0) return "Sees no projects";

  // Without the space's project list there are only identifiers, which name
  // nothing to a reader; the count is the part of the answer that is true.
  if (projects === undefined) {
    return `Sees ${ids.length} ${ids.length === 1 ? "project" : "projects"}`;
  }

  const named = ids
    .map((id) => projects.find((project) => project.id === id)?.name)
    .filter((name) => name !== undefined);
  const unlisted = ids.length - named.length;

  const parts = [...named];
  if (unlisted > 0) parts.push(`${unlisted} archived or removed`);
  return `Sees ${parts.join(", ")}`;
}

/** Set equality: the API stores an allow-list, and its order carries nothing. */
function sameIds(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id));
}

/**
 * Whether an invitation has run out, which is not the same question as what its
 * status says (ADR 000100).
 *
 * The worker writes `expired` on an hourly sweep, so for up to an hour an
 * invitation nobody can take up is still stored as `pending`. Filing those
 * under Pending would be wrong for an hour at a time, which is the defect this
 * is here to fix in miniature, so the clock is read alongside the status. This
 * is `Invite.Redeemable` in the browser's own words.
 */
function ranOut(invite: Invite): boolean {
  if (invite.status === "expired") return true;
  return invite.status === "pending" && Date.parse(invite.expiresAt) <= Date.now();
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function describe(error: unknown): string {
  if (error instanceof ApiError) return error.userMessage;
  return error instanceof Error ? error.message : "Something went wrong.";
}
